/** The dates each calendar view reads, shared by CalendarPage and the background
 *  warm so a warmed events feed carries exactly the key the page asks for. */

import { addDays, endOfDay, endOfMonth, endOfWeek, startOfDay, startOfMonth, startOfWeek } from 'date-fns';
import { CalendarService } from '../../services/CalendarService';
import type { CalendarFeedScope } from '../../services/CalendarFeedCache';
import type { CalendarView } from '../../types/calendar';

/** Mobile (and iPad portrait) gets the agenda view by default: far more
 *  scannable than the 7-column week grid. Threshold aligned with CLAUDE.md's
 *  canonical <1024 mobile cut. */
export const defaultGridView = (): CalendarView => {
  if (typeof window === 'undefined') return 'week';
  return window.innerWidth < 1024 ? 'agenda' : 'week';
};

export function calendarRange(view: CalendarView, date: Date, isMobile: boolean): { rangeStart: Date; rangeEnd: Date } {
  if (view === 'agenda') return {
    rangeStart: startOfDay(date),
    rangeEnd: endOfDay(addDays(date, 30)),
  };
  if (view === 'day') return { rangeStart: startOfDay(date), rangeEnd: endOfDay(date) };
  if (view === 'week') return isMobile ? {
    rangeStart: startOfDay(date),
    rangeEnd: endOfDay(addDays(date, 1)),
  } : {
    rangeStart: startOfWeek(date, { weekStartsOn: 1 }),
    rangeEnd: endOfWeek(date, { weekStartsOn: 1 }),
  };
  // Expand to cover the full Mon–Sun grid that MonthView renders —
  // the grid includes days from the surrounding weeks.
  return {
    rangeStart: startOfWeek(startOfMonth(date), { weekStartsOn: 1 }),
    rangeEnd: endOfWeek(endOfMonth(date), { weekStartsOn: 1 }),
  };
}

/** The events feed the personal calendar reads first when it opens today. */
export function openingCalendarEventsFeed(userId: string, workspaceId: string): { scope: CalendarFeedScope; load: () => ReturnType<typeof CalendarService.getEvents> } {
  // useIsMobile() answers false on the page's first render, and only the week view reads it.
  const { rangeStart, rangeEnd } = calendarRange(defaultGridView(), new Date(), false);
  return {
    scope: { userId, workspaceId, from: rangeStart.toISOString(), to: rangeEnd.toISOString() },
    load: () => CalendarService.getEvents(userId, rangeStart, rangeEnd),
  };
}
